import { useCallback, useEffect, useState } from 'react';
import { local } from 'shared/utils/storage/local-storage';
import { session } from 'shared/utils/storage/session-storage';

/**
 * Returns the current value associated with the given key
 * stored in Web Storage API and two function to update
 * or remove the key/value pair.
 */
export function useStorage<T>(
  key: string,
  useLocal = false,
  defaultValue: any = undefined,
): [T | null, (newValue: T) => void, () => void] {
  const storage = useLocal ? local : session;
  const [value, setValue] = useState<T | null>(storage.get(key) ?? defaultValue);
  const setItem = useCallback(
    (newValue: T) => {
      storage.set(key, newValue);
    },
    [key, storage],
  );

  const removeItem = useCallback(() => {
    storage.remove(key);
  }, [key, storage]);

  useEffect(() => {
    const listener = (newKey, newValue) => {
      if (key === newKey) {
        setValue(newValue);
      }
    };
    return storage.events.on('update', listener);
  }, [key, storage.events, value]);

  return [value, setItem, removeItem];
}
